<template>
  <div class="workspace-view">
    <!-- Hero Section -->
    <section class="relative pt-32 pb-20 bg-[#faf6df]">
      <div class="container mx-auto px-4 relative">
        <div class="text-center mb-16">
          <h1 class="text-5xl font-bold text-[#0f4640] mb-6 animate-fade-in">{{ $t('workspace.hero.title') }}</h1>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed animate-fade-in-delay">
            {{ $t('workspace.hero.description') }}
          </p>
        </div>

        <!-- Feature Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <!-- Task Management -->
          <div class="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-12 h-12 bg-[#0f4640] rounded-lg flex items-center justify-center">
                <i class="fas fa-tasks text-[#faf6df] text-xl"></i>
              </div>
              <h3 class="text-xl font-bold text-[#0f4640]">{{ $t('workspace.features.taskManagement.title') }}</h3>
            </div>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">{{ $t('workspace.features.taskManagement.description') }}</p>
          </div>

          <!-- Progress Analysis -->
          <div class="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-12 h-12 bg-[#0f4640] rounded-lg flex items-center justify-center">
                <i class="fas fa-chart-line text-[#faf6df] text-xl"></i>
              </div>
              <h3 class="text-xl font-bold text-[#0f4640]">{{ $t('workspace.features.progressAnalysis.title') }}</h3>
            </div>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">{{ $t('workspace.features.progressAnalysis.description') }}</p>
          </div>

          <!-- Team Collaboration -->
          <div class="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-12 h-12 bg-[#0f4640] rounded-lg flex items-center justify-center">
                <i class="fas fa-users text-[#faf6df] text-xl"></i>
              </div>
              <h3 class="text-xl font-bold text-[#0f4640]">{{ $t('workspace.features.teamCollaboration.title') }}</h3>
            </div>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">{{ $t('workspace.features.teamCollaboration.description') }}</p>
          </div>

          <!-- Document Management -->
          <div class="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-12 h-12 bg-[#0f4640] rounded-lg flex items-center justify-center">
                <i class="fas fa-folder-open text-[#faf6df] text-xl"></i>
              </div>
              <h3 class="text-xl font-bold text-[#0f4640]">{{ $t('workspace.features.documentManagement.title') }}</h3>
            </div>
            <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">{{ $t('workspace.features.documentManagement.description') }}</p>
          </div>
        </div>

        <!-- Main Interface Preview -->
        <div class="bg-white rounded-3xl shadow-2xl p-8 mb-16 transform hover:-translate-y-1 transition-all duration-300">
          <img 
            src="@/assets/All-In-One Workspace.png" 
            :alt="$t('workspace.preview.imageAlt')" 
            class="w-full rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
          />
        </div>

        <!-- Call to Action -->
        <div class="text-center">
          <router-link 
            to="/kanban-board" 
            class="btn-primary inline-flex items-center gap-3"
          >
            {{ $t('workspace.cta.discover') }}
            <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Kanban Board Section -->
    <section class="relative py-20 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-[#0f4640] mb-6">{{ $t('workspace.kanban.title') }}</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            {{ $t('workspace.kanban.description') }}
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <!-- Features List -->
          <div class="space-y-8" :class="{ 'text-right': $i18n.locale === 'ar' }">
            <div class="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
              <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('workspace.kanban.features.newBoard.title') }}</h3>
              <p class="text-gray-600">{{ $t('workspace.kanban.features.newBoard.description') }}</p>
            </div>

            <div class="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
              <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('workspace.kanban.features.cards.title') }}</h3>
              <p class="text-gray-600">{{ $t('workspace.kanban.features.cards.description') }}</p>
            </div>

            <div class="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
              <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('workspace.kanban.features.columns.title') }}</h3>
              <p class="text-gray-600">{{ $t('workspace.kanban.features.columns.description') }}</p>
            </div>

            <div class="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
              <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('workspace.kanban.features.filters.title') }}</h3>
              <p class="text-gray-600">{{ $t('workspace.kanban.features.filters.description') }}</p>
            </div>
          </div>

          <!-- Interactive Demo -->
          <div class="bg-white rounded-2xl p-8 shadow-xl">
            <div class="flex justify-between items-center mb-6">
              <button class="btn-primary">
                {{ $t('workspace.kanban.demo.addColumn') }}
              </button>
              <div class="flex gap-4">
                <button class="btn-secondary">
                  {{ $t('workspace.kanban.demo.saveFilter') }}
                </button>
                <button class="btn-secondary">
                  {{ $t('workspace.kanban.demo.addTask') }}
                </button>
              </div>
            </div>

            <div class="flex gap-6 overflow-x-auto pb-4 scrollbar-hide">
              <!-- To Do Column -->
              <div class="flex-shrink-0 w-72 bg-gray-50 rounded-lg p-4">
                <div class="flex justify-between items-center mb-4">
                  <h4 class="font-bold text-[#0f4640]">{{ $t('workspace.kanban.demo.columns.todo.title') }}</h4>
                  <span class="bg-gray-200 px-2 py-1 rounded-full text-sm">3</span>
                </div>
                <div class="space-y-3">
                  <div class="bg-white p-4 rounded-lg shadow cursor-pointer hover:shadow-md">
                    <h5 class="font-bold mb-2">{{ $t('workspace.kanban.demo.columns.todo.tasks.task1.title') }}</h5>
                    <p class="text-sm text-gray-600">{{ $t('workspace.kanban.demo.columns.todo.tasks.task1.description') }}</p>
                  </div>
                  <div class="bg-white p-4 rounded-lg shadow cursor-pointer hover:shadow-md">
                    <h5 class="font-bold mb-2">{{ $t('workspace.kanban.demo.columns.todo.tasks.task2.title') }}</h5>
                    <p class="text-sm text-gray-600">{{ $t('workspace.kanban.demo.columns.todo.tasks.task2.description') }}</p>
                  </div>
                </div>
              </div>

              <!-- In Progress Column -->
              <div class="flex-shrink-0 w-72 bg-gray-50 rounded-lg p-4">
                <div class="flex justify-between items-center mb-4">
                  <h4 class="font-bold text-[#0f4640]">{{ $t('workspace.kanban.demo.columns.inProgress.title') }}</h4>
                  <span class="bg-gray-200 px-2 py-1 rounded-full text-sm">2</span>
                </div>
                <div class="space-y-3">
                  <div class="bg-white p-4 rounded-lg shadow cursor-pointer hover:shadow-md">
                    <h5 class="font-bold mb-2">{{ $t('workspace.kanban.demo.columns.inProgress.tasks.task1.title') }}</h5>
                    <p class="text-sm text-gray-600">{{ $t('workspace.kanban.demo.columns.inProgress.tasks.task1.description') }}</p>
                  </div>
                </div>
              </div>

              <!-- Done Column -->
              <div class="flex-shrink-0 w-72 bg-gray-50 rounded-lg p-4">
                <div class="flex justify-between items-center mb-4">
                  <h4 class="font-bold text-[#0f4640]">{{ $t('workspace.kanban.demo.columns.done.title') }}</h4>
                  <span class="bg-gray-200 px-2 py-1 rounded-full text-sm">1</span>
                </div>
                <div class="space-y-3">
                  <div class="bg-white p-4 rounded-lg shadow cursor-pointer hover:shadow-md">
                    <h5 class="font-bold mb-2">{{ $t('workspace.kanban.demo.columns.done.tasks.task1.title') }}</h5>
                    <p class="text-sm text-gray-600">{{ $t('workspace.kanban.demo.columns.done.tasks.task1.description') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Agile Section -->
    <section class="relative py-20 bg-white">
      <div class="container mx-auto px-4">
        <div class="flex flex-col lg:flex-row items-center gap-12">
          <!-- Content Side -->
          <div class="lg:w-1/2" :class="{ 'text-right': $i18n.locale === 'ar' }">
            <h2 class="text-4xl font-bold text-[#0f4640] mb-6">
              {{ $t('workspace.agile.title') }}
            </h2>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              {{ $t('workspace.agile.description') }}
            </p>
            <router-link 
              to="/agile" 
              class="btn-primary inline-flex items-center gap-2"
            >
              {{ $t('workspace.agile.readMore') }}
              <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
            </router-link>
          </div>
          <!-- Image Side -->
          <div class="lg:w-1/2">
            <img 
              src="@/assets/Agile.png" 
              :alt="$t('workspace.agile.title')" 
              class="rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 w-full"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- To-Do List Section -->
    <section class="relative py-20 bg-gradient-to-br from-[#faf6df] to-[#f8f9ff]">
      <div class="container mx-auto px-4">
        <div class="flex flex-col lg:flex-row-reverse items-center gap-12">
          <!-- Content Side -->
          <div class="lg:w-1/2" :class="{ 'text-right': $i18n.locale === 'ar' }">
            <h2 class="text-4xl font-bold text-[#0f4640] mb-6">
              {{ $t('workspace.todoAndChat.title') }}
            </h2>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              {{ $t('workspace.todoAndChat.description') }}
            </p>
            <router-link 
              to="/contact" 
              class="btn-primary inline-flex items-center gap-2"
            >
              {{ $t('workspace.todoAndChat.tryNow') }}
              <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
            </router-link>
          </div>
          <!-- Image Side -->
          <div class="lg:w-1/2">
            <img 
              src="@/assets/To-Do.png" 
              :alt="$t('workspace.todoAndChat.title')" 
              class="rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 w-full"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Document Storage Section -->
    <section class="relative py-20 bg-white">
      <div class="container mx-auto px-4">
        <div class="flex flex-col lg:flex-row items-center gap-12">
          <!-- Content Side -->
          <div class="lg:w-1/2" :class="{ 'text-right': $i18n.locale === 'ar' }">
            <h2 class="text-4xl font-bold text-[#0f4640] mb-6">
              {{ $t('workspace.documentStorage.title') }}
            </h2>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              {{ $t('workspace.documentStorage.description') }}
            </p>
            <div class="bg-[#faf6df] rounded-xl p-6 mb-8">
              <h3 class="text-xl font-bold text-[#0f4640] mb-4">{{ $t('workspace.documentStorage.features.title') }}</h3>
              <ul class="space-y-3 text-gray-700">
                <li class="flex items-center gap-3">
                  <i class="fas fa-check-circle text-[#0f4640]"></i>
                  <span>{{ $t('workspace.documentStorage.features.list.multiUpload') }}</span>
                </li>
                <li class="flex items-center gap-3">
                  <i class="fas fa-check-circle text-[#0f4640]"></i>
                  <span>{{ $t('workspace.documentStorage.features.list.folderOrganization') }}</span>
                </li>
                <li class="flex items-center gap-3">
                  <i class="fas fa-check-circle text-[#0f4640]"></i>
                  <span>{{ $t('workspace.documentStorage.features.list.easySharing') }}</span>
                </li>
                <li class="flex items-center gap-3">
                  <i class="fas fa-check-circle text-[#0f4640]"></i>
                  <span>{{ $t('workspace.documentStorage.features.list.versionControl') }}</span>
                </li>
              </ul>
            </div>
          </div>
          <!-- Image Side -->
          <div class="lg:w-1/2">
            <img 
              src="@/assets/documents-storage.png" 
              :alt="$t('workspace.documentStorage.title')" 
              class="rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 w-full"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Email Management Section -->
    <section class="relative py-20 bg-gradient-to-br from-[#faf6df] to-[#f8f9ff]">
      <div class="container mx-auto px-4">
        <div class="flex flex-col lg:flex-row items-center gap-12">
          <!-- Image Side -->
          <div class="lg:w-1/2">
            <img 
              src="@/assets/Emails.png" 
              :alt="$t('workspace.emails.title')" 
              class="rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 w-full"
              loading="lazy"
            />
          </div>
          <!-- Content Side -->
          <div class="lg:w-1/2" :class="{ 'text-right': $i18n.locale === 'ar' }">
            <h2 class="text-6xl font-bold text-[#0f4640] mb-6">{{ $t('workspace.emails.title') }}</h2>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              {{ $t('workspace.emails.description') }}
            </p>
            <router-link 
              to="/email" 
              class="btn-primary inline-flex items-center gap-2"
            >
              {{ $t('workspace.emails.readMore') }}
              <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'WorkspaceView'
}
</script>

<style scoped>
.workspace-view {
  min-height: 100vh;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in {
  animation: fadeIn 0.6s ease-out forwards;
}

.animate-fade-in-delay {
  animation: fadeIn 0.6s ease-out 0.3s forwards;
  opacity: 0;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Smooth hover transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}
</style> 